import { AppProvider } from '@shopify/polaris';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getAdminLocale,
  setAdminLocale,
  type AdminLocale,
} from './index';
import { polarisI18n } from './polaris';

interface LocaleContextValue {
  locale: AdminLocale;
  setLocale: (next: AdminLocale) => void;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'en',
  setLocale: () => undefined,
});

export function useAdminLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

/**
 * Owns Polaris + Freebi locale. Changing language remounts the app tree
 * so existing t() calls pick up the new dictionary without a full reload.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AdminLocale>(() => getAdminLocale());

  const setLocale = useCallback((next: AdminLocale) => {
    if (next === getAdminLocale()) return;
    setAdminLocale(next);
    setLocaleState(next);
  }, []);

  const value = useMemo(
    () => ({ locale, setLocale }),
    [locale, setLocale],
  );

  return (
    <LocaleContext.Provider value={value}>
      <AppProvider i18n={polarisI18n(locale)}>{children}</AppProvider>
    </LocaleContext.Provider>
  );
}
